import { useTranslation } from 'react-i18next';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import WhatsAppFloat from '../../home/components/WhatsAppFloat';
import SEO from '@/components/SEO';

export default function SantePage() {
  const { t } = useTranslation('navigation');
  const isEn = t('home') === 'Home';

  const solutions = [
    {
      titleFr: 'Dossiers Patients Électroniques', titleEn: 'Electronic Patient Records',
      descFr: 'Gestion sécurisée et complète des dossiers médicaux', descEn: 'Secure and complete medical record management',
      icone: 'ri-file-user-line',
      featuresFr: ['Dossiers patients numériques sécurisés', 'Historique médical complet', 'Prescriptions électroniques', "Résultats d'examens intégrés", 'Partage sécurisé entre praticiens'],
      featuresEn: ['Secure digital patient records', 'Complete medical history', 'Electronic prescriptions', 'Integrated exam results', 'Secure sharing between practitioners'],
    },
    {
      titleFr: 'Gestion des Rendez-vous', titleEn: 'Appointment Management',
      descFr: 'Planification optimisée des consultations médicales', descEn: 'Optimized scheduling of medical consultations',
      icone: 'ri-calendar-check-line',
      featuresFr: ['Agenda médical intelligent', 'Prise de RDV en ligne', 'Rappels automatiques patients', 'Gestion des urgences', 'Optimisation des créneaux'],
      featuresEn: ['Smart medical calendar', 'Online appointment booking', 'Automatic patient reminders', 'Emergency management', 'Slot optimization'],
    },
    {
      titleFr: 'Facturation Médicale', titleEn: 'Medical Billing',
      descFr: 'Gestion financière adaptée au secteur de la santé', descEn: 'Financial management adapted to the healthcare sector',
      icone: 'ri-calculator-line',
      featuresFr: ['Facturation automatique des actes', 'Gestion des assurances', 'Tiers payant intégré', 'Suivi des paiements', 'Rapports financiers détaillés'],
      featuresEn: ['Automatic medical act billing', 'Insurance management', 'Integrated third-party payment', 'Payment tracking', 'Detailed financial reports'],
    },
    {
      titleFr: 'Pharmacie Intégrée', titleEn: 'Integrated Pharmacy',
      descFr: 'Gestion complète de la pharmacie et des stocks', descEn: 'Complete pharmacy and stock management',
      icone: 'ri-capsule-line',
      featuresFr: ['Inventaire médicaments temps réel', 'Gestion des péremptions', 'Alertes de stock minimum', 'Traçabilité des lots', 'Interface avec les prescriptions'],
      featuresEn: ['Real-time medication inventory', 'Expiry management', 'Minimum stock alerts', 'Batch traceability', 'Prescription interface'],
    },
    {
      titleFr: 'Laboratoire & Imagerie', titleEn: 'Laboratory & Imaging',
      descFr: 'Gestion des examens et résultats médicaux', descEn: 'Management of medical exams and results',
      icone: 'ri-test-tube-line',
      featuresFr: ["Gestion des demandes d'examens", 'Saisie des résultats', 'Archivage des images médicales', 'Transmission sécurisée', 'Analyses statistiques'],
      featuresEn: ['Exam request management', 'Result entry', 'Medical image archiving', 'Secure transmission', 'Statistical analysis'],
    },
    {
      titleFr: 'Télémédecine', titleEn: 'Telemedicine',
      descFr: 'Consultations à distance et suivi patients', descEn: 'Remote consultations and patient monitoring',
      icone: 'ri-video-line',
      featuresFr: ['Consultations vidéo sécurisées', 'Suivi à distance des patients', 'Partage de documents médicaux', 'Prescriptions électroniques', 'Historique des téléconsultations'],
      featuresEn: ['Secure video consultations', 'Remote patient monitoring', 'Medical document sharing', 'Electronic prescriptions', 'Teleconsultation history'],
    },
  ];

  const advantages = [
    { icon: 'ri-shield-check-line', titleKey: 'santeAdv1Title', descKey: 'santeAdv1Desc' },
    { icon: 'ri-speed-up-line', titleKey: 'santeAdv2Title', descKey: 'santeAdv2Desc' },
    { icon: 'ri-heart-pulse-line', titleKey: 'santeAdv3Title', descKey: 'santeAdv3Desc' },
    { icon: 'ri-customer-service-line', titleKey: 'santeAdv4Title', descKey: 'santeAdv4Desc' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Logiciel Santé - Alpha Solutions | Dossiers Médicaux & Gestion Clinique"
        description="Solution complète pour cliniques et pharmacies : dossiers patients électroniques, rendez-vous, facturation médicale, pharmacie intégrée, laboratoire et télémédecine."
        keywords="logiciel santé Cameroun, dossier médical électronique Douala, gestion clinique Afrique, software médical"
        canonicalPath="/secteurs/sante"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Logiciel Santé Alpha Solutions",
          "description": "Solution de gestion médicale pour cliniques et pharmacies au Cameroun",
          "brand": { "@type": "Organization", "name": "Alpha Solutions" }
        }}
      />
      <Header />
      
      <section className="relative bg-cover bg-center py-20"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6),rgba(0,0,0,0.6)), url('https://readdy.ai/api/search-image?query=modern%20african%20medical%20clinic%20with%20advanced%20patient%20management%20system%20and%20digital%20healthcare%20technology%2C%20professional%20healthcare%20facility%20with%20electronic%20medical%20records%20and%20telemedicine%20solutions&width=1200&height=600&seq=sante-hero&orientation=landscape')` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">{t('santeHeroTitle')}</h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">{t('santeHeroDesc')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://wa.me/237693385267" target="_blank" rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-whatsapp-line mr-2"></i>{t('sectorDemoBtn')}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('santeSolutionsTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('santeSolutionsSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((sol, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mb-4">
                  <i className={`${sol.icone} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{isEn ? sol.titleEn : sol.titleFr}</h3>
                <p className="text-gray-600 mb-4">{isEn ? sol.descEn : sol.descFr}</p>
                <ul className="space-y-2">
                  {(isEn ? sol.featuresEn : sol.featuresFr).map((f, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-2 mt-1"></i>
                      <span className="text-sm text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('santeWhyTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((adv, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${adv.icon} text-2xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t(adv.titleKey)}</h3>
                <p className="text-gray-600">{t(adv.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 rounded-lg p-8 border-l-4 border-blue-500">
            <div className="flex items-center mb-6">
              <img src="https://readdy.ai/api/search-image?query=professional%20african%20medical%20doctor%20Christian%20Pany%20in%20modern%20healthcare%20facility%2C%20confident%20physician%20managing%20successful%20medical%20practice%20with%20digital%20health%20technology%20solutions&width=100&height=100&seq=christian-pany-biomedicam&orientation=squarish"
                alt={t('santeTestiName')} className="w-16 h-16 rounded-full object-cover mr-4" />
              <div>
                <h3 className="text-xl font-bold text-blue-900">{t('santeTestiName')}</h3>
                <p className="text-blue-700">{t('santeTestiRole')}</p>
              </div>
            </div>
            <blockquote className="text-lg text-blue-800 italic mb-4">"{t('santeTestiQuote')}"</blockquote>
            <div className="flex items-center">
              <div className="flex text-yellow-400">{[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill"></i>)}</div>
              <span className="ml-2 text-blue-700 font-medium">{t('santeSatisfied')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-blue-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('santeCtaTitle')}</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">{t('santeCtaDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wa.me/237693385267" target="_blank" rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line mr-2"></i>{t('sectorFreeDemo')}
            </a>
            <a href="/solutions" className="bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
              {t('sectorViewSolutions')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
